import React, { useEffect, useState } from 'react';
import FormSelect from '../../common/FormSelect/FormSelect';
import Loading from '../../common/Loading/Loading';
import { createPlanSelectFormatters } from '../../common/PlanNameWithProject/PlanNameWithProject';
import { SubscriptionPlanService } from '../../../services';
import { SubscriptionPlan } from '../../../types';
import { formatCurrency } from '../../../utils';
import { validateCpf } from '../../../utils/validation';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import {
  GATEWAY_PROVIDERS,
  PAYMENT_SYSTEM_LABELS,
  type GatewayProvider,
} from '../../../types/payment';
import styles from './CreatePaymentAccountModal.module.css';

const PAYMENT_SYSTEM_OPTIONS = [
  ...GATEWAY_PROVIDERS.map((provider) => ({
    value: provider,
    label: PAYMENT_SYSTEM_LABELS[provider],
  })),
  { value: 'pix', label: PAYMENT_SYSTEM_LABELS.pix },
];

export type CreatePaymentAccountSelection =
  | { system: GatewayProvider }
  | { system: 'pix'; planId: number };

type AccountSystemOption = GatewayProvider | 'pix';

interface CreatePaymentAccountModalProps {
  open: boolean;
  loading: boolean;
  onConfirm: (selection: CreatePaymentAccountSelection) => void;
  onCancel: () => void;
  studentName: string;
  studentCpf: string;
  studentEmail: string;
  title?: string;
  message?: string;
  confirmLabel?: string;
  lockedSystem?: 'pix';
}

const ASAAS_REQUIRED_FIELDS: Array<{ key: 'studentName' | 'studentCpf' | 'studentEmail'; label: string }> = [
  { key: 'studentName', label: 'Nome' },
  { key: 'studentCpf', label: 'CPF' },
  { key: 'studentEmail', label: 'E-mail' },
];

const STRIPE_REQUIRED_FIELDS: Array<{ key: 'studentName' | 'studentEmail'; label: string }> = [
  { key: 'studentName', label: 'Nome' },
  { key: 'studentEmail', label: 'E-mail' },
];

const PAYPAL_REQUIRED_FIELDS: Array<{ key: 'studentName' | 'studentEmail'; label: string }> = [
  { key: 'studentName', label: 'Nome' },
  { key: 'studentEmail', label: 'E-mail' },
];

const CreatePaymentAccountModal: React.FC<CreatePaymentAccountModalProps> = ({
  open,
  loading,
  onConfirm,
  onCancel,
  studentName,
  studentCpf,
  studentEmail,
  title = 'Criar conta de pagamento',
  message = 'Selecione o sistema de pagamento para esta aluna.',
  confirmLabel = 'Confirmar',
  lockedSystem,
}) => {
  const [selectedSystem, setSelectedSystem] = useState<AccountSystemOption | ''>(lockedSystem ?? '');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedSystem(lockedSystem ?? '');
      setSelectedPlanId('');
      return;
    }

    setSelectedSystem(lockedSystem ?? '');
  }, [lockedSystem, open]);

  const isPix = selectedSystem === 'pix';

  useEffect(() => {
    if (!open || !isPix) {
      return;
    }

    let active = true;
    setPlansLoading(true);
    SubscriptionPlanService.getSelectablePlans()
      .then((nextPlans) => {
        if (active) {
          setPlans(nextPlans);
        }
      })
      .finally(() => {
        if (active) {
          setPlansLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [isPix, open]);

  const fieldValues = { studentName, studentCpf, studentEmail };
  const requiredFields = selectedSystem === 'asaas'
    ? ASAAS_REQUIRED_FIELDS
    : selectedSystem === 'stripe'
    ? STRIPE_REQUIRED_FIELDS
    : selectedSystem === 'paypal'
      ? PAYPAL_REQUIRED_FIELDS
    : [];

  const asaasCpfError = selectedSystem === 'asaas'
    ? validateCpf(studentCpf, { required: true })
    : '';

  const missingFields = requiredFields
    .filter(({ key }) => {
      if (key === 'studentCpf') {
        return Boolean(asaasCpfError);
      }
      return !fieldValues[key]?.trim();
    })
    .map(({ label }) => label);

  const blockingMessage = asaasCpfError
    || (missingFields.length > 0
      ? `Preencha os seguintes campos antes de continuar: ${missingFields.join(', ')}`
      : '');

  const selectedPlan = plans.find((plan) => String(plan.id) === selectedPlanId) ?? null;
  const planOptions = plans.map((plan) => ({ value: String(plan.id), label: plan.name }));
  const planSelectFormatters = createPlanSelectFormatters(plans);
  const pixReady = isPix && Boolean(selectedPlanId) && !plansLoading;

  useEscapeToClose(onCancel, open && !loading);

  if (!open) return null;

  const handleConfirm = () => {
    if (!selectedSystem || blockingMessage) {
      return;
    }

    if (selectedSystem === 'pix') {
      const planId = Number(selectedPlanId);
      if (!Number.isInteger(planId) || planId <= 0) {
        return;
      }
      onConfirm({ system: 'pix', planId });
      return;
    }

    onConfirm({ system: selectedSystem });
  };

  return (
    <div className={styles.overlay}>
      <div
        className={`${styles.modal} ${isPix ? styles.modalWide : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles['icon-circle']}>
          <span className="material-symbols-outlined">account_balance_wallet</span>
        </div>

        <h3 className={styles.title}>{title}</h3>
        <p className={styles.message}>{message}</p>

        {!lockedSystem && (
          <div className={styles['select-wrap']}>
            <FormSelect
              id="payment-system"
              label="Sistema de pagamento"
              options={PAYMENT_SYSTEM_OPTIONS}
              value={selectedSystem}
              onChange={(e) => {
                setSelectedSystem(e.target.value as AccountSystemOption);
                setSelectedPlanId('');
              }}
              disabled={loading}
            />
          </div>
        )}

        {isPix && (
          <div className={styles['select-wrap']}>
            <p className={styles.message}>
              Pix direto na conta da consultoria. A aluna fica com assinatura ativa, sem conta em gateway.
            </p>
            {plansLoading ? (
              <Loading />
            ) : plans.length === 0 ? (
              <p className={styles.validation}>Nenhum plano disponível para esta conta.</p>
            ) : (
              <FormSelect
                id="pix-payment-plan"
                label="Plano"
                options={planOptions}
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
                disabled={loading}
                triggerFormat={planSelectFormatters.triggerFormat}
                optionFormat={planSelectFormatters.optionFormat}
              />
            )}
            {selectedPlan && (
              <p className={styles.planValue}>
                Mensalidade: <strong>{formatCurrency(selectedPlan.price_cents / 100)}</strong>
              </p>
            )}
          </div>
        )}

        {blockingMessage && (
          <p className={styles.validation}>
            {asaasCpfError
              ? (
                <>
                  CPF obrigatório para Asaas: <strong>{asaasCpfError}</strong>
                  {' '}Atualize o cadastro da aluna antes de continuar.
                </>
              )
              : (
                <>
                  Preencha os seguintes campos antes de continuar: <strong>{missingFields.join(', ')}</strong>
                </>
              )}
          </p>
        )}

        <div className={styles.actions}>
          <button
            className={`${styles.btn} ${styles['btn-cancel']}`}
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className={`${styles.btn} ${styles['btn-confirm']}`}
            onClick={handleConfirm}
            disabled={loading || !selectedSystem || Boolean(blockingMessage) || (isPix && !pixReady)}
          >
            {loading ? 'Aguarde...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreatePaymentAccountModal;
